import { Card, Group, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';

/**
 * StatCard — dashboard headline number with a brand-tinted left border.
 *
 * Stack: large value, label below in `c="ink.6"`, optional supporting line
 * (`hint`) and an optional trailing slot (`right`) for icons / sparklines.
 */
export function StatCard({
  label,
  value,
  hint,
  right,
  emphasis = 'brand',
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  right?: ReactNode;
  emphasis?: 'brand' | 'sun' | 'ink';
}) {
  const accentVar =
    emphasis === 'sun'
      ? 'var(--mantine-color-sun-5)'
      : emphasis === 'ink'
        ? 'light-dark(var(--mantine-color-gray-4), var(--mantine-color-dark-4))'
        : 'var(--mantine-color-brand-6)';

  return (
    <Card
      withBorder
      padding="lg"
      radius="md"
      style={{
        height: '100%',
        background: 'light-dark(var(--mantine-color-body), var(--mantine-color-dark-6))',
        borderColor: 'light-dark(var(--mantine-color-ink-1), var(--mantine-color-dark-4))',
        borderLeft: `3px solid ${accentVar}`,
      }}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Text size="sm" c="dimmed" fw={500}>
            {label}
          </Text>
          <Text
            fw={600}
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', lineHeight: 1.1 }}
          >
            {value}
          </Text>
          {hint ? (
            <Text size="xs" c="dimmed">
              {hint}
            </Text>
          ) : null}
        </Stack>
        {right ? <Group gap={0}>{right}</Group> : null}
      </Group>
    </Card>
  );
}
